import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  tenantIdentityProvidersApi,
  type SaveTenantIdentityProviderInput,
  type TenantIdentityProvider,
  type TenantOIDCRoleMappingCollection,
  type TenantIdentityProvidersApi,
} from './tenantIdentityProvidersApi';
import { useTenantIdentityProvidersAccess } from './useTenantIdentityProvidersAccess';
import { buildTenantOidcClientSettings, type TenantOidcClientSettings } from './tenantOidcClientSettings';

export interface TenantIdentityProviderDraft {
  providerId?: string;
  version?: number;
  displayName: string;
  issuer: string;
  clientId: string;
  emailClaim: string;
  displayNameClaim: string;
  roleClaimPath: string;
  additionalScopes: string;
  replaceSecret: boolean;
  clientSecret: string;
}

export type TenantIdentityProviderValidation = Partial<Record<'displayName' | 'issuer' | 'clientId' | 'emailClaim' | 'roleClaimPath' | 'additionalScopes' | 'clientSecret' | 'general', string>>;

export interface TenantOIDCRoleMappingDraft {
  externalRole: string;
  tenantRoleId: string;
}

export interface TenantIdentityProvidersViewModel {
  tenantId: string;
  tenantLabel: string;
  accessDenied: boolean;
  canManage: boolean;
  initialized: boolean;
  loading: boolean;
  error: string | null;
  clientSettings: TenantOidcClientSettings | null;
  providers: Array<{
    id: string;
    displayName: string;
    issuer: string;
    loginHostnameLabel: string;
    desiredState: TenantIdentityProvider['desiredState'];
    observedState: TenantIdentityProvider['observedState'];
    secretPresent: boolean;
    secretFingerprint: string | null;
    diagnosticCode: string | null;
    validatedAt: string | null;
    lastSuccessfulLoginAt: string | null;
    version: number;
    busyAction: string | null;
    onEdit: () => void;
    onValidate: () => void;
    onActivate: () => void;
    onDisable: () => void;
    onDelete: () => void;
  }>;
  editor: {
    open: boolean;
    mode: 'create' | 'edit';
    draft: TenantIdentityProviderDraft;
    validation: TenantIdentityProviderValidation;
    submitting: boolean;
    secretPresent: boolean;
    onOpenChange: (open: boolean) => void;
    onDraftChange: (draft: TenantIdentityProviderDraft) => void;
    onSubmit: () => void;
  };
  roleMappings: {
	canManage: boolean;
    loading: boolean;
    submitting: boolean;
    version: number;
    rows: TenantOIDCRoleMappingDraft[];
    availableRoles: TenantOIDCRoleMappingCollection['availableRoles'];
    onChange: (rows: TenantOIDCRoleMappingDraft[]) => void;
    onSave: () => void;
  };
  onCreate: () => void;
  onRefresh: () => void;
}

const emptyDraft = (): TenantIdentityProviderDraft => ({
  displayName: '', issuer: '', clientId: '', emailClaim: 'email', displayNameClaim: 'name', roleClaimPath: '', additionalScopes: '', replaceSecret: true, clientSecret: '',
});

export function validateTenantIdentityProviderDraft(draft: TenantIdentityProviderDraft): TenantIdentityProviderValidation {
  const validation: TenantIdentityProviderValidation = {};
  if (!draft.displayName.trim()) validation.displayName = 'Display name is required.';
  if (!draft.clientId.trim()) validation.clientId = 'Client ID is required.';
  if (!/^[A-Za-z0-9_.-]+$/.test(draft.emailClaim.trim())) validation.emailClaim = 'Use a valid claim name.';
  if (draft.roleClaimPath.trim() && !/^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){0,7}$/.test(draft.roleClaimPath.trim())) validation.roleClaimPath = 'Use a dotted claim path with up to eight segments.';
  const scopes = draft.additionalScopes.split(/[\s,]+/).filter(Boolean);
  if (scopes.length > 16 || scopes.includes('openid') || scopes.some((scope) => !/^[A-Za-z0-9._:-]{1,64}$/.test(scope))) validation.additionalScopes = 'Enter up to 16 valid scopes; openid is included automatically.';
  try {
    const issuer = new URL(draft.issuer.trim());
    if (issuer.protocol !== 'https:' || issuer.username || issuer.password || issuer.search || issuer.hash || (issuer.port && issuer.port !== '443')) throw new Error();
  } catch {
    validation.issuer = 'Use an exact HTTPS issuer URL without credentials, query, fragment, or a non-standard port.';
  }
  if (draft.replaceSecret && !draft.clientSecret) validation.clientSecret = 'Client secret is required.';
  return validation;
}

export function useTenantIdentityProvidersController(api: TenantIdentityProvidersApi = tenantIdentityProvidersApi): TenantIdentityProvidersViewModel {
  const access = useTenantIdentityProvidersAccess();
  const [providers, setProviders] = useState<TenantIdentityProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedTenantId, setLoadedTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<TenantIdentityProviderDraft>(emptyDraft);
  const [validation, setValidation] = useState<TenantIdentityProviderValidation>({});
  const [submitting, setSubmitting] = useState(false);
  const [roleMappingCollection, setRoleMappingCollection] = useState<TenantOIDCRoleMappingCollection | null>(null);
  const [roleMappingRows, setRoleMappingRows] = useState<TenantOIDCRoleMappingDraft[]>([]);
  const [roleMappingsLoading, setRoleMappingsLoading] = useState(false);
  const [roleMappingsSubmitting, setRoleMappingsSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!access.canView || !access.tenantId) {
      setProviders([]); setLoading(false); setLoadedTenantId(null); setError(null); return;
    }
    setLoading(true); setError(null);
    try {
      const loaded = await api.list(access.tenantId);
      setProviders(loaded);
      setLoadedTenantId(access.tenantId);
      if (loaded[0]) {
        setRoleMappingsLoading(true);
        const collection = await api.listRoleMappings(access.tenantId, loaded[0].id);
        setRoleMappingCollection(collection);
        setRoleMappingRows(collection.mappings.map(({ externalRole, tenantRoleId }) => ({ externalRole, tenantRoleId })));
      } else {
        setRoleMappingCollection(null);
        setRoleMappingRows([]);
      }
    }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Identity providers could not be loaded.'); }
    finally { setLoading(false); setRoleMappingsLoading(false); }
  }, [access.canView, access.tenantId, api]);

  useEffect(() => { void load(); }, [load]);

  const submit = useCallback(async () => {
    if (!access.canManage || !access.tenantId) return;
    const nextValidation = validateTenantIdentityProviderDraft(draft);
    setValidation(nextValidation);
    if (Object.keys(nextValidation).length) return;
    setSubmitting(true);
    const input: SaveTenantIdentityProviderInput = {
      displayName: draft.displayName.trim(), issuer: draft.issuer.trim().replace(/\/$/, ''), clientId: draft.clientId.trim(),
      emailClaim: draft.emailClaim.trim(), displayNameClaim: draft.displayNameClaim.trim() || null, version: draft.version,
      roleClaimPath: draft.roleClaimPath.trim() || null,
      additionalScopes: draft.additionalScopes.split(/[\s,]+/).map((scope) => scope.trim()).filter(Boolean),
    };
    try {
      let saved: TenantIdentityProvider;
      if (draft.providerId) {
        saved = await api.update(access.tenantId, draft.providerId, input);
        if (draft.replaceSecret) saved = await api.replaceSecret(access.tenantId, saved.id, draft.clientSecret, saved.version);
      } else {
        saved = await api.create(access.tenantId, { ...input, clientSecret: draft.clientSecret });
      }
      setDraft(emptyDraft());
      setEditorOpen(false);
      await load();
    } catch (reason) {
      setValidation({ general: reason instanceof Error ? reason.message : 'Identity provider could not be saved.' });
    } finally {
      // Secrets are write-only: clear them after every submission attempt and never hydrate them from API data.
      setDraft((current) => ({ ...current, clientSecret: '' }));
      setSubmitting(false);
    }
  }, [access.canManage, access.tenantId, api, draft, load]);

  const runAction = useCallback(async (provider: TenantIdentityProvider, action: 'validate' | 'activate' | 'disable' | 'delete') => {
    if (!access.canManage || !access.tenantId) return;
    setBusyAction(`${action}:${provider.id}`); setError(null);
    try {
      if (action === 'delete') await api.delete(access.tenantId, provider.id, provider.version);
      else await api.action(access.tenantId, provider.id, action, provider.version);
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Identity provider action failed.'); }
    finally { setBusyAction(null); }
  }, [access.canManage, access.tenantId, api, load]);

  const openEditor = useCallback((provider?: TenantIdentityProvider) => {
    if (!access.canManage) return;
    setValidation({});
    setDraft(provider ? {
      providerId: provider.id, version: provider.version, displayName: provider.displayName, issuer: provider.issuer,
      clientId: provider.clientId, emailClaim: provider.emailClaim, displayNameClaim: provider.displayNameClaim ?? '',
      roleClaimPath: provider.roleClaimPath ?? '', additionalScopes: provider.additionalScopes.join(' '),
      replaceSecret: false, clientSecret: '',
    } : emptyDraft());
    setEditorOpen(true);
  }, [access.canManage]);

  const saveRoleMappings = useCallback(async () => {
    const provider = providers[0];
    if (!access.canManageRoleMappings || !access.tenantId || !provider || !roleMappingCollection) return;
    const mappings = roleMappingRows.map((row) => ({ externalRole: row.externalRole.trim(), tenantRoleId: row.tenantRoleId }));
    if (mappings.some((row) => !row.externalRole || !row.tenantRoleId)) {
      setError('Every role mapping needs an upstream role and a tenant role.');
      return;
    }
    setRoleMappingsSubmitting(true); setError(null);
    try {
      const collection = await api.replaceRoleMappings(access.tenantId, provider.id, roleMappingCollection.version, mappings);
      setRoleMappingCollection(collection);
      setRoleMappingRows(collection.mappings.map(({ externalRole, tenantRoleId }) => ({ externalRole, tenantRoleId })));
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Role mappings could not be saved.');
    } finally { setRoleMappingsSubmitting(false); }
  }, [access.canManageRoleMappings, access.tenantId, api, load, providers, roleMappingCollection, roleMappingRows]);

  return useMemo(() => ({
    tenantId: access.tenantId, tenantLabel: access.tenantLabel, accessDenied: !access.canView, canManage: access.canManage,
    initialized: !access.canView || loadedTenantId === access.tenantId, loading, error,
    clientSettings: buildTenantOidcClientSettings(
      access.tenantId,
      typeof window === 'undefined' ? null : { protocol: window.location.protocol, hostname: window.location.hostname },
      providers.length > 0,
    ),
    providers: providers.map((provider) => ({
      id: provider.id, displayName: provider.displayName, issuer: provider.issuer, loginHostnameLabel: provider.loginHostnameLabel,
      desiredState: provider.desiredState, observedState: provider.observedState, secretPresent: provider.secretPresent,
      secretFingerprint: provider.secretFingerprint ?? null, diagnosticCode: provider.diagnosticCode ?? null,
      validatedAt: provider.validatedAt ?? null, lastSuccessfulLoginAt: provider.lastSuccessfulLoginAt ?? null,
      version: provider.version, busyAction: busyAction?.endsWith(`:${provider.id}`) ? busyAction.split(':')[0] : null,
      onEdit: () => openEditor(provider), onValidate: () => void runAction(provider, 'validate'),
      onActivate: () => void runAction(provider, 'activate'), onDisable: () => void runAction(provider, 'disable'),
      onDelete: () => void runAction(provider, 'delete'),
    })),
    editor: {
      open: editorOpen, mode: draft.providerId ? 'edit' as const : 'create' as const, draft, validation, submitting,
      secretPresent: Boolean(providers.find((item) => item.id === draft.providerId)?.secretPresent),
      onOpenChange: (open: boolean) => { setEditorOpen(open); if (!open) setDraft(emptyDraft()); },
      onDraftChange: setDraft, onSubmit: () => void submit(),
    },
    roleMappings: {
      canManage: access.canManageRoleMappings,
      loading: roleMappingsLoading, submitting: roleMappingsSubmitting, version: roleMappingCollection?.version ?? 0,
      rows: roleMappingRows, availableRoles: roleMappingCollection?.availableRoles ?? [], onChange: setRoleMappingRows,
      onSave: () => void saveRoleMappings(),
    },
    onCreate: () => openEditor(), onRefresh: () => void load(),
  }), [access, busyAction, draft, editorOpen, error, load, loadedTenantId, loading, openEditor, providers, roleMappingCollection, roleMappingRows, roleMappingsLoading, roleMappingsSubmitting, runAction, saveRoleMappings, submit, submitting, validation]);
}
